import Link from 'next/link'
import { FC } from 'react'
import { Icon } from '@/root/ui/shared'
import { FooterLinkAndInfoContentItem } from '@/root/shared-types'
import { useCustomRouter } from '@/root/shared-hooks'
import cn from 'classnames'

interface LinksAndInfoItemProps {
  items: FooterLinkAndInfoContentItem[]
  icon?: string
  useOldBoraOrigin?: boolean
  title?: string
}

const LinksAndInfoItem: FC<LinksAndInfoItemProps> = ({ items, title, icon }) => {
  const { locale } = useCustomRouter()
  const isExternalLink = (link: string) => /^(https?:|mailto:|tel:)/.test(link)

  return (
    <div className="z-10 text-white">
      {title && <h6 className="mb-7 text-lg leading-snug font-semibold md:mb-8">{title}</h6>}

      {icon && (
        <div className="mb-4 w-fit md:mb-5">
          <Icon alt={icon} type={icon} width={28} height={28} />
        </div>
      )}

      <div className="text-primary-base flex flex-col gap-2 leading-6 text-white/90">
        {items.map(({ title, link, decoration, linkType, withBottomMargin }) => {
          if (title instanceof Array) {
            const [label, linkValueFromTitle] = title

            const href = (() => {
              if (linkType) {
                return `${linkType}:${linkValueFromTitle}`
              }

              if (link) {
                return isExternalLink(link) ? link : `/${locale}${link}`
              }

              return ''
            })()

            return (
              <div
                key={label}
                className={cn(
                  'flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1',
                  withBottomMargin && 'mt-4'
                )}
              >
                <span className="text-white/85">{label}</span>

                {linkType || link ? (
                  <Link
                    href={href}
                    prefetch={false}
                    target={link && isExternalLink(link) ? '_blank' : '_self'}
                    className={cn(
                      'font-medium text-white underline-offset-4',
                      'hover:text-white hover:underline',
                      decoration === 'bold' && 'font-semibold'
                    )}
                  >
                    {linkValueFromTitle}
                  </Link>
                ) : (
                  <span className="font-medium text-white/95">{linkValueFromTitle}</span>
                )}
              </div>
            )
          }

          const isInnerHeading = decoration === 'bold' && !link
          const key = `${title}${withBottomMargin ? '-second' : ''}`

          return (
            <div key={key} className={cn(withBottomMargin && 'mt-4', isInnerHeading && 'mt-6')}>
              {link ? (
                <Link
                  href={isExternalLink(link) ? link : `/${locale}${link}`}
                  prefetch={false}
                  target={isExternalLink(link) ? '_blank' : '_self'}
                  className="text-white/90 underline-offset-4 hover:text-white hover:underline"
                >
                  <span className="text-base-primary leading-6">{title}</span>
                </Link>
              ) : (
                <span
                  className={cn(
                    isInnerHeading
                      ? 'text-lg leading-snug font-semibold text-white'
                      : 'text-base leading-6 text-white/90'
                  )}
                >
                  {title}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default LinksAndInfoItem
